import { useState, useMemo, useRef, useEffect } from "react";
import "./SKUManagements.css";
import api from "./../../../axiosInstance.js"
import logger from "../../../utils/logger";

// ─── Constants ────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
    id: "", name: "", type: "Service", category: "",
    unitPrice: "", cgst: "", sgst: "", igst: "", doctor: "", description: "",
};

const normalizeSkus = (raw) =>
    (raw || []).map(s => ({
        _id: s._id,
        id: s.clinicSkuId,
        name: s.name,
        type: s.type || "Service",
        category: s.category || "",
        unitPrice: s.unitPrice,
        cgst: s.tax?.cgst ?? 0,
        sgst: s.tax?.sgst ?? 0,
        igst: s.tax?.igst ?? 0,
        doctor: s.doctor || "",
        description: s.description || "",
        active: s.isActive,
    }));

const normalizeApptTypes = (raw) =>
    (raw || []).map(t => ({
        _id: t._id,
        name: t.name,
        color: t.color || "#1d9e75",
        duration: t.duration || null,
        defaultSkus: (t.defaultSkus || []).map(ds => ({
            skuId:    ds.skuId?._id || ds.skuId,
            skuName:  ds.skuId?.name || "",
            qty:      ds.qty || 1,
            discount: ds.discount || 0,
        })),
        isActive: t.isActive !== false,
    }));

// ─── Toast hook ───────────────────────────────────────────────────────────────
function useToast() {
    const [msg, setMsg] = useState("");
    const [visible, setVisible] = useState(false);
    const timerRef = useRef(null);
    const show = (text) => {
        clearTimeout(timerRef.current);
        setMsg(text);
        setVisible(true);
        timerRef.current = setTimeout(() => setVisible(false), 2800);
    };
    return { msg, visible, show };
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ iconBg, iconStroke, iconPath, label, value, sub }) {
    return (
        <div className="sku-stat-card">
            <div className="sku-stat-icon" style={{ background: iconBg }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                    stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    {iconPath}
                </svg>
            </div>
            <div className="sku-stat-label">{label}</div>
            <div className="sku-stat-value">{value}</div>
            <div className="sku-stat-sub">{sub}</div>
        </div>
    );
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ on, onToggle }) {
    return (
        <button
            onClick={onToggle}
            className={`sku-toggle ${on ? "sku-toggle--on" : "sku-toggle--off"}`}
        >
            <div className={`sku-toggle-thumb ${on ? "sku-toggle-thumb--on" : "sku-toggle-thumb--off"}`} />
        </button>
    );
}

// ─── FormGroup ────────────────────────────────────────────────────────────────
function FormGroup({ label, children }) {
    return (
        <div className="sku-form-group">
            <label className="sku-form-label">{label}</label>
            {children}
        </div>
    );
}

// ─── SKU Modal ────────────────────────────────────────────────────────────────
function SkuModal({ editingSku, onClose, onSave, existingIds, categories }) {
    const [form, setForm] = useState(
        editingSku
            ? { ...editingSku, unitPrice: String(editingSku.unitPrice), cgst: String(editingSku.cgst), sgst: String(editingSku.sgst), igst: String(editingSku.igst || "") }
            : { ...EMPTY_FORM, category: categories[0] || "" }
    );
    const [errors, setErrors] = useState({});

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: "" }));
    };

    const validate = () => {
        const e = {};
        if (!form.id.trim()) e.id = "Required";
        if (!editingSku && existingIds.includes(form.id.trim())) e.id = "ID already exists";
        if (!form.name.trim()) e.name = "Required";
        if (!form.unitPrice || isNaN(parseFloat(form.unitPrice)) || parseFloat(form.unitPrice) < 0) e.unitPrice = "Enter valid price";
        return e;
    };

    const handleSave = () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        onSave({
            id: form.id.trim(), name: form.name.trim(), type: form.type,
            category: form.category, unitPrice: parseFloat(form.unitPrice),
            cgst: parseFloat(form.cgst) || 0, sgst: parseFloat(form.sgst) || 0,
            igst: parseFloat(form.igst) || 0, doctor: form.doctor.trim(),
            description: form.description.trim(), active: editingSku ? editingSku.active : true,
        });
    };

    const inp = (key, opts = {}) => (
        <input
            value={form[key]}
            onChange={e => set(key, e.target.value)}
            className={`sku-input${errors[key] ? " sku-input--error" : ""}`}
            disabled={!!editingSku && key === "id"}
            {...opts}
        />
    );

    const sel = (key, options) => (
        <select value={form[key]} onChange={e => set(key, e.target.value)} className="sku-input">
            {options.map(o => <option key={o}>{o}</option>)}
        </select>
    );

    return (
        <div className="sku-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="sku-modal">
                <div className="sku-modal-head">
                    <span className="sku-modal-title">{editingSku ? "Edit SKU" : "Add new SKU"}</span>
                    <button onClick={onClose} className="sku-modal-close">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="sku-modal-body">
                    <div className="sku-form-row">
                        <FormGroup label="SKU ID *">
                            {inp("id", { placeholder: "e.g. SKU011" })}
                            {errors.id && <span className="sku-err">{errors.id}</span>}
                        </FormGroup>
                        <FormGroup label="Name *">
                            {inp("name", { placeholder: "Service or product name" })}
                            {errors.name && <span className="sku-err">{errors.name}</span>}
                        </FormGroup>
                    </div>

                    <div className="sku-form-row">
                        <FormGroup label="Type">{sel("type", ["Service", "Product", "Package"])}</FormGroup>
                        <FormGroup label="Category">
                            <>
                                <input
                                    list="sku-category-list"
                                    value={form.category}
                                    onChange={e => set("category", e.target.value)}
                                    placeholder="Select or type a category"
                                    className="sku-input"
                                />
                                <datalist id="sku-category-list">
                                    {categories.map(c => <option key={c} value={c} />)}
                                </datalist>
                            </>
                        </FormGroup>
                    </div>

                    <div className="sku-form-row">
                        <FormGroup label="Unit price (₹) *">
                            {inp("unitPrice", { type: "number", placeholder: "0", min: "0" })}
                            {errors.unitPrice && <span className="sku-err">{errors.unitPrice}</span>}
                        </FormGroup>
                        <FormGroup label="Linked doctor (optional)">
                            {inp("doctor", { placeholder: "Doctor name" })}
                        </FormGroup>
                    </div>

                    <FormGroup label="Tax rates (%)">
                        <div className="sku-tax-row">
                            <FormGroup label={<span className="sku-form-label--muted">CGST</span>}>
                                {inp("cgst", { type: "number", placeholder: "0", min: "0", max: "28" })}
                            </FormGroup>
                            <FormGroup label={<span className="sku-form-label--muted">SGST</span>}>
                                {inp("sgst", { type: "number", placeholder: "0", min: "0", max: "28" })}
                            </FormGroup>
                            <FormGroup label={<span className="sku-form-label--muted">IGST</span>}>
                                {inp("igst", { type: "number", placeholder: "0", min: "0", max: "28" })}
                            </FormGroup>
                        </div>
                    </FormGroup>

                    <FormGroup label="Description">
                        <textarea
                            value={form.description}
                            onChange={e => set("description", e.target.value)}
                            placeholder="Optional description or notes"
                            className="sku-input sku-textarea"
                        />
                    </FormGroup>
                </div>

                <div className="sku-modal-foot">
                    <button onClick={onClose} className="sku-btn-cancel">Cancel</button>
                    <button onClick={handleSave} className="sku-btn-save">Save SKU</button>
                </div>
            </div>
        </div>
    );
}

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
function DeleteModal({ sku, onClose, onConfirm }) {
    return (
        <div className="sku-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="sku-modal sku-modal--sm">
                <div className="sku-modal-body">
                    <div className="sku-delete-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                    </div>
                    <div className="sku-delete-title">Delete SKU?</div>
                    <div className="sku-delete-body">
                        This will permanently remove <strong>"{sku?.name}"</strong>. This action cannot be undone.
                    </div>
                </div>
                <div className="sku-modal-foot">
                    <button onClick={onClose} className="sku-btn-cancel">Cancel</button>
                    <button onClick={onConfirm} className="sku-btn-save sku-btn-save--danger">Delete SKU</button>
                </div>
            </div>
        </div>
    );
}

// ─── ApptTypeModal ────────────────────────────────────────────────────────────
function ApptTypeModal({ editingType, skuList, onClose, onSave }) {
    const [name,     setName]     = useState(editingType?.name || "");
    const [color,    setColor]    = useState(editingType?.color || "#1d9e75");
    const [duration, setDuration] = useState(editingType?.duration ?? "");
    const [defaults, setDefaults] = useState(
        editingType?.defaultSkus?.map(ds => ({
            skuId:    ds.skuId,
            skuName:  ds.skuName || skuList.find(s => s._id === ds.skuId)?.name || "",
            qty:      ds.qty || 1,
            discount: ds.discount || 0,
        })) || []
    );
    const [pickerSkuId, setPickerSkuId] = useState("");
    const [nameErr, setNameErr] = useState("");

    const addDefaultSku = () => {
        if (!pickerSkuId) return;
        if (defaults.find(d => d.skuId === pickerSkuId)) { setPickerSkuId(""); return; }
        const sku = skuList.find(s => s._id === pickerSkuId);
        if (!sku) return;
        setDefaults(prev => [...prev, { skuId: sku._id, skuName: sku.name, qty: 1, discount: 0 }]);
        setPickerSkuId("");
    };

    const removeDefault = (i) => setDefaults(prev => prev.filter((_, idx) => idx !== i));

    const updateDefault = (i, key, val) =>
        setDefaults(prev => prev.map((d, idx) => idx === i ? { ...d, [key]: val } : d));

    const handleSave = () => {
        if (!name.trim()) { setNameErr("Name is required"); return; }
        onSave({
            name:        name.trim(),
            color,
            duration:    duration !== "" ? Number(duration) : undefined,
            defaultSkus: defaults.map(d => ({
                skuId:    d.skuId,
                qty:      d.qty,
                discount: d.discount,
            })),
        });
    };

    const availableSkus = skuList.filter(s => s.active && !defaults.find(d => d.skuId === s._id));

    return (
        <div className="sku-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="sku-modal apt-modal">
                <div className="sku-modal-head">
                    <span className="sku-modal-title">
                        {editingType ? "Edit appointment type" : "New appointment type"}
                    </span>
                    <button onClick={onClose} className="sku-modal-close">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="sku-modal-body">
                    {/* Name + color */}
                    <div className="sku-form-row">
                        <FormGroup label="Type name *">
                            <input
                                value={name}
                                onChange={e => { setName(e.target.value); setNameErr(""); }}
                                placeholder="e.g. Follow-up Consultation"
                                className={`sku-input${nameErr ? " sku-input--error" : ""}`}
                            />
                            {nameErr && <span className="sku-err">{nameErr}</span>}
                        </FormGroup>
                        <FormGroup label="Duration (min)">
                            <input
                                type="number" min="1" value={duration}
                                onChange={e => setDuration(e.target.value)}
                                placeholder="e.g. 30"
                                className="sku-input"
                            />
                        </FormGroup>
                    </div>

                    {/* Color picker */}
                    <FormGroup label="Calendar colour">
                        <div className="apt-color-row">
                            {["#1d9e75","#378add","#f5c842","#e24b4a","#8b5cf6","#f97316","#1a2a3a","#6b7280"].map(c => (
                                <button
                                    key={c}
                                    onClick={() => setColor(c)}
                                    className={`apt-color-swatch${color === c ? " apt-color-swatch--active" : ""}`}
                                    style={{ background: c }}
                                />
                            ))}
                            <input
                                type="color" value={color}
                                onChange={e => setColor(e.target.value)}
                                className="apt-color-custom"
                                title="Custom colour"
                            />
                        </div>
                    </FormGroup>

                    {/* Default SKUs */}
                    <div>
                        <div className="sku-form-label" style={{ marginBottom: 8 }}>Default services / items</div>
                        <div className="apt-default-skus-box">
                            {defaults.length === 0 && (
                                <div className="apt-no-skus">No default items — add below</div>
                            )}
                            {defaults.map((d, i) => (
                                <div key={d.skuId} className="apt-default-sku-row">
                                    <div className="apt-default-sku-name">{d.skuName}</div>
                                    <div className="apt-default-sku-controls">
                                        <label className="apt-sku-ctrl-label">Qty</label>
                                        <input
                                            type="number" min="1" max="99" value={d.qty}
                                            onChange={e => updateDefault(i, "qty", Math.max(1, parseInt(e.target.value) || 1))}
                                            className="apt-num-inp"
                                        />
                                        <label className="apt-sku-ctrl-label">Disc ₹</label>
                                        <input
                                            type="number" min="0" value={d.discount}
                                            onChange={e => updateDefault(i, "discount", Math.max(0, parseFloat(e.target.value) || 0))}
                                            className="apt-num-inp"
                                        />
                                        <button onClick={() => removeDefault(i)} className="apt-remove-btn">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" strokeWidth="2" strokeLinecap="round">
                                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="apt-add-sku-row">
                            <select
                                value={pickerSkuId}
                                onChange={e => setPickerSkuId(e.target.value)}
                                className="sku-input apt-sku-picker"
                            >
                                <option value="">— Add a service or product —</option>
                                {availableSkus.map(s => (
                                    <option key={s._id} value={s._id}>
                                        {s.name} — ₹{s.unitPrice.toLocaleString("en-IN")}
                                    </option>
                                ))}
                            </select>
                            <button onClick={addDefaultSku} className="apt-btn-add-sku" disabled={!pickerSkuId}>
                                + Add
                            </button>
                        </div>
                    </div>
                </div>

                <div className="sku-modal-foot">
                    <button onClick={onClose} className="sku-btn-cancel">Cancel</button>
                    <button onClick={handleSave} className="sku-btn-save">Save type</button>
                </div>
            </div>
        </div>
    );
}

// ─── ApptTypeCard ─────────────────────────────────────────────────────────────
function ApptTypeCard({ type, onEdit, onToggle, onDelete }) {
    return (
        <div className={`apt-type-card${!type.isActive ? " apt-type-card--inactive" : ""}`}>
            <div className="apt-type-card-head">
                <div className="apt-type-card-name-row">
                    <span className="apt-type-color-dot" style={{ background: type.color }} />
                    <span className="apt-type-name">{type.name}</span>
                    {type.duration && (
                        <span className="apt-type-duration">{type.duration} min</span>
                    )}
                </div>
                <div className="apt-type-card-actions">
                    <Toggle on={type.isActive} onToggle={() => onToggle(type)} />
                    <button onClick={() => onEdit(type)} className="sku-action-btn" title="Edit">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                    </button>
                    <button onClick={() => onDelete(type)} className="sku-action-btn" title="Delete">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                    </button>
                </div>
            </div>
            <div className="apt-type-skus">
                {type.defaultSkus.length === 0 ? (
                    <span className="apt-no-default-label">No default items</span>
                ) : (
                    type.defaultSkus.map((ds, i) => (
                        <span key={i} className="apt-sku-chip">
                            {ds.skuName || "SKU"} ×{ds.qty}
                        </span>
                    ))
                )}
            </div>
        </div>
    );
}

// ─── DeleteApptTypeModal ──────────────────────────────────────────────────────
function DeleteApptTypeModal({ type, onClose, onConfirm }) {
    return (
        <div className="sku-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="sku-modal sku-modal--sm">
                <div className="sku-modal-body">
                    <div className="sku-delete-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b91c1c" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                    </div>
                    <div className="sku-delete-title">Delete appointment type?</div>
                    <div className="sku-delete-body">
                        This will remove <strong>"{type?.name}"</strong>. Existing appointments will not be affected.
                    </div>
                </div>
                <div className="sku-modal-foot">
                    <button onClick={onClose} className="sku-btn-cancel">Cancel</button>
                    <button onClick={onConfirm} className="sku-btn-save sku-btn-save--danger">Delete</button>
                </div>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function SKUManagements() {
    const clinicId = localStorage.getItem("selectedClinic");

    // Tab
    const [activeTab, setActiveTab] = useState("skus");

    // SKU state
    const [skus, setSkus] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, avg: 0, cats: [] });
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [catFilter, setCatFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editingSku, setEditingSku] = useState(null);
    const [deleteSku, setDeleteSku] = useState(null);

    // Appointment types state
    const [apptTypes, setApptTypes]           = useState([]);
    const [apptTypesLoading, setApptTypesLoading] = useState(true);
    const [apptTypeModal, setApptTypeModal]   = useState(null); // null | { mode: "add"|"edit", type? }
    const [deleteApptType, setDeleteApptType] = useState(null);

    const toast = useToast();

    // ── SKU fetch ──
    const fetchSkus = async () => {
        const res = await api.get("/clinicSKU", { params: { clinicId, limit: 1000, page: 1 } });
        setSkus(normalizeSkus(res.data.data));
    };

    const fetchCategories = async () => {
        const res = await api.get("/clinicSKU/categories", { params: { clinicId } });
        setCategories(res.data.data || []);
    };

    const fetchStats = async () => {
        const res = await api.get("/clinicSKU/stats", { params: { clinicId } });
        const d = res.data.data || {};
        setStats({
            total: d.totalSkus || 0,
            active: d.activeSkus || 0,
            inactive: d.inactiveSkus || 0,
            avg: Math.round(d.avgUnitPrice || 0),
            cats: d.categoryList || [],
        });
    };

    const refreshAll = () => Promise.all([fetchSkus(), fetchStats(), fetchCategories()]);

    // ── Appt types fetch ──
    const fetchApptTypes = async () => {
        setApptTypesLoading(true);
        try {
            const res = await api.get("/appointmentTypes", { params: { clinicId, limit: 100, page: 1 } });
            setApptTypes(normalizeApptTypes(res.data.data));
        } catch (err) {
            logger.error("Failed to load appointment types:", err);
        } finally {
            setApptTypesLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([refreshAll(), fetchApptTypes()]).finally(() => setLoading(false));
    }, []);

    // ── SKU actions ──
    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return skus.filter(s => {
            const mQ = !q || s.name.toLowerCase().includes(q) || s.id.toLowerCase().includes(q);
            const mT = !typeFilter || s.type === typeFilter;
            const mC = !catFilter || s.category === catFilter;
            const mS = !statusFilter || (statusFilter === "active" && s.active) || (statusFilter === "inactive" && !s.active);
            return mQ && mT && mC && mS;
        });
    }, [skus, search, typeFilter, catFilter, statusFilter]);

    const openAdd = () => { setEditingSku(null); setModalOpen(true); };
    const openEdit = (sku) => { setEditingSku(sku); setModalOpen(true); };
    const closeModal = () => { setModalOpen(false); setEditingSku(null); };

    const handleSave = async (data) => {
        try {
            if (editingSku) {
                await api.put(`/clinicSKU/${editingSku._id}`, {
                    name: data.name, unitPrice: data.unitPrice, type: data.type,
                    category: data.category, description: data.description,
                    tax: { cgst: data.cgst, sgst: data.sgst, igst: data.igst },
                    isActive: data.active,
                });
                toast.show(`"${data.name}" updated`);
            } else {
                await api.post("/clinicSKU", {
                    clinicSkuId: data.id, clinicId, name: data.name,
                    unitPrice: data.unitPrice, type: data.type, category: data.category,
                    description: data.description,
                    tax: { cgst: data.cgst, sgst: data.sgst, igst: data.igst },
                });
                toast.show(`"${data.name}" created`);
            }
            closeModal();
            await refreshAll();
        } catch (err) {
            toast.show(err.response?.data?.message || "Failed to save SKU");
        }
    };

    const handleToggle = async (sku) => {
        try {
            await api.patch(`/clinicSKU/${sku._id}/toggle`);
            toast.show(`${sku.name} ${sku.active ? "deactivated" : "activated"}`);
            await Promise.all([fetchSkus(), fetchStats()]);
        } catch (err) {
            toast.show("Failed to update SKU status");
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.delete(`/clinicSKU/${deleteSku._id}`);
            toast.show(`"${deleteSku.name}" deleted`);
            setDeleteSku(null);
            await refreshAll();
        } catch (err) {
            toast.show(err.response?.data?.message || "Failed to delete SKU");
        }
    };

    // ── Appt type actions ──
    const handleApptTypeSave = async (data) => {
        try {
            if (apptTypeModal?.mode === "edit") {
                await api.put(`/appointmentTypes/${apptTypeModal.type._id}`, data);
                toast.show(`"${data.name}" updated`);
            } else {
                await api.post("/appointmentTypes", { clinicId, ...data });
                toast.show(`"${data.name}" created`);
            }
            setApptTypeModal(null);
            await fetchApptTypes();
        } catch (err) {
            toast.show(err.response?.data?.message || "Failed to save appointment type");
        }
    };

    const handleApptTypeToggle = async (type) => {
        try {
            await api.patch(`/appointmentTypes/${type._id}/toggle`);
            toast.show(`"${type.name}" ${type.isActive ? "deactivated" : "activated"}`);
            await fetchApptTypes();
        } catch (err) {
            toast.show("Failed to update appointment type");
        }
    };

    const handleApptTypeDeleteConfirm = async () => {
        try {
            await api.delete(`/appointmentTypes/${deleteApptType._id}`);
            toast.show(`"${deleteApptType.name}" deleted`);
            setDeleteApptType(null);
            await fetchApptTypes();
        } catch (err) {
            toast.show(err.response?.data?.message || "Failed to delete appointment type");
        }
    };

    const typePillClass = (type) => ({
        Service: "sku-pill--service",
        Product: "sku-pill--product",
        Package: "sku-pill--package",
    })[type] || "";

    return (
        <div className="sku-managements__main-container">
            <div className="sku-main-wrapper">
                <main className="sku-content">

                    {/* ── Page header ── */}
                    <div className="sku-page-header">
                        <div>
                            <h1 className="sku-page-title">Services &amp; SKU management</h1>
                            <p className="sku-page-sub">Manage clinic services, products, packages and appointment types</p>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            {activeTab === "skus" && (
                                <button onClick={openAdd} className="sku-btn-add">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a2a3a" strokeWidth="2.2" strokeLinecap="round">
                                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add new SKU
                                </button>
                            )}
                            {activeTab === "types" && (
                                <button onClick={() => setApptTypeModal({ mode: "add" })} className="sku-btn-add">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1a2a3a" strokeWidth="2.2" strokeLinecap="round">
                                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add appointment type
                                </button>
                            )}
                        </div>
                    </div>

                    {/* ── Tabs ── */}
                    <div className="sku-tabs">
                        <button
                            className={`sku-tab${activeTab === "skus" ? " sku-tab--active" : ""}`}
                            onClick={() => setActiveTab("skus")}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
                            </svg>
                            Services &amp; SKUs
                            <span className="sku-tab-count">{skus.length}</span>
                        </button>
                        <button
                            className={`sku-tab${activeTab === "types" ? " sku-tab--active" : ""}`}
                            onClick={() => setActiveTab("types")}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
                            </svg>
                            Appointment types
                            <span className="sku-tab-count">{apptTypes.length}</span>
                        </button>
                    </div>

                    {/* ── SKUs tab ── */}
                    {activeTab === "skus" && (
                        <>
                            {/* Stats */}
                            <div className="sku-stats-grid">
                                <StatCard
                                    iconBg="#e1f5ee" iconStroke="#0f6e56"
                                    iconPath={<><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></>}
                                    label="Total SKUs" value={stats.total} sub="across all types"
                                />
                                <StatCard
                                    iconBg="#dcfce7" iconStroke="#16a34a"
                                    iconPath={<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>}
                                    label="Active SKUs" value={stats.active} sub={`${stats.inactive} inactive`}
                                />
                                <StatCard
                                    iconBg="#dbeafe" iconStroke="#185fa5"
                                    iconPath={<path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />}
                                    label="Avg unit price" value={`₹${stats.avg.toLocaleString("en-IN")}`} sub="across active SKUs"
                                />
                                <StatCard
                                    iconBg="#fef3c7" iconStroke="#b45309"
                                    iconPath={<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>}
                                    label="Categories" value={stats.cats.length}
                                    sub={stats.cats.slice(0, 3).join(", ") + (stats.cats.length > 3 ? "…" : "")}
                                />
                            </div>

                            {/* Toolbar */}
                            <div className="sku-toolbar">
                                <div className="sku-search-box">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.8" strokeLinecap="round">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input
                                        value={search} onChange={e => setSearch(e.target.value)}
                                        placeholder="Search by name or SKU ID…"
                                        className="sku-search-input"
                                    />
                                </div>
                                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="sku-filter-select">
                                    {["", "Service", "Product", "Package"].map((o, i) => (
                                        <option key={o} value={o}>{["All types", "Service", "Product", "Package"][i]}</option>
                                    ))}
                                </select>
                                <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="sku-filter-select">
                                    <option value="">All categories</option>
                                    {categories.map(c => <option key={c}>{c}</option>)}
                                </select>
                                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="sku-filter-select">
                                    {["", "active", "inactive"].map((o, i) => (
                                        <option key={o} value={o}>{["All status", "Active", "Inactive"][i]}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Table */}
                            <div className="sku-table-wrap">
                                <table className="sku-table">
                                    <thead>
                                        <tr>
                                            {["Name / ID", "Type", "Category", "Unit price", "Tax (CGST/SGST)", "Linked doctor", "Status", "Actions"].map(h => (
                                                <th key={h} className="sku-th">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan={8} className="sku-empty">Loading…</td></tr>
                                        ) : filtered.length === 0 ? (
                                            <tr><td colSpan={8} className="sku-empty">No SKUs match your filters</td></tr>
                                        ) : filtered.map(s => (
                                            <tr key={s._id}>
                                                <td className="sku-td">
                                                    <div className="sku-sku-name">{s.name}</div>
                                                    <div className="sku-sku-id">{s.id}</div>
                                                </td>
                                                <td className="sku-td">
                                                    <span className={`sku-pill ${typePillClass(s.type)}`}>{s.type}</span>
                                                </td>
                                                <td className="sku-td sku-td--small">{s.category}</td>
                                                <td className="sku-td sku-td--bold">₹{s.unitPrice.toLocaleString("en-IN")}</td>
                                                <td className="sku-td sku-td--small">{s.cgst}% / {s.sgst}%{s.igst ? ` / ${s.igst}%` : ""}</td>
                                                <td className="sku-td sku-td--small">{s.doctor || "—"}</td>
                                                <td className="sku-td"><Toggle on={s.active} onToggle={() => handleToggle(s)} /></td>
                                                <td className="sku-td">
                                                    <div className="sku-action-btns">
                                                        <button onClick={() => openEdit(s)} className="sku-action-btn" title="Edit">
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                            </svg>
                                                        </button>
                                                        <button onClick={() => setDeleteSku(s)} className="sku-action-btn" title="Delete">
                                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#e24b4a" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6" />
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* ── Appointment types tab ── */}
                    {activeTab === "types" && (
                        <>
                            {apptTypesLoading ? (
                                <div className="sku-empty" style={{ paddingTop: 60 }}>Loading…</div>
                            ) : apptTypes.length === 0 ? (
                                <div className="apt-empty-state">
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>
                                    </svg>
                                    <div className="apt-empty-title">No appointment types yet</div>
                                    <div className="apt-empty-sub">Create types like "Follow-up" or "First-time consultation" and link default services to them.</div>
                                    <button onClick={() => setApptTypeModal({ mode: "add" })} className="sku-btn-add" style={{ marginTop: 16 }}>
                                        + Add first type
                                    </button>
                                </div>
                            ) : (
                                <div className="apt-types-grid">
                                    {apptTypes.map(t => (
                                        <ApptTypeCard
                                            key={t._id}
                                            type={t}
                                            onEdit={type => setApptTypeModal({ mode: "edit", type })}
                                            onToggle={handleApptTypeToggle}
                                            onDelete={setDeleteApptType}
                                        />
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                </main>
            </div>

            {/* ── SKU Modals ── */}
            {modalOpen && (
                <SkuModal
                    editingSku={editingSku}
                    onClose={closeModal}
                    onSave={handleSave}
                    existingIds={skus.map(s => s.id)}
                    categories={categories}
                />
            )}
            {deleteSku && (
                <DeleteModal sku={deleteSku} onClose={() => setDeleteSku(null)} onConfirm={handleDeleteConfirm} />
            )}

            {/* ── Appt type modals ── */}
            {apptTypeModal && (
                <ApptTypeModal
                    editingType={apptTypeModal.mode === "edit" ? apptTypeModal.type : null}
                    skuList={skus}
                    onClose={() => setApptTypeModal(null)}
                    onSave={handleApptTypeSave}
                />
            )}
            {deleteApptType && (
                <DeleteApptTypeModal
                    type={deleteApptType}
                    onClose={() => setDeleteApptType(null)}
                    onConfirm={handleApptTypeDeleteConfirm}
                />
            )}

            {/* ── Toast ── */}
            <div className={`sku-toast${toast.visible ? " sku-toast--visible" : ""}`}>
                {toast.msg}
            </div>
        </div>
    );
}
